/**
 * The front page. The first thing the game shows, and the only screen that is a
 * menu rather than a heads-up display.
 *
 * Laid out in two columns instead of one centred stack, because the halves
 * answer different questions: on the left "what is this and how do I start it",
 * on the right "how am I doing and who am I racing". A single column made the
 * start prompt compete with the leaderboard for the same eye.
 *
 * Pointer lock decides which version of the left column is showing. With the
 * mouse free this is a menu you click; once the mouse is in the game there is
 * nothing left to click, so the buttons give way to the one instruction that
 * still applies.
 *
 * The buttons are also reachable from the keyboard - arrows move the highlight,
 * Enter presses it - because this is the one screen a player meets before the
 * mouse is theirs to aim with, and because a speedrunner restarting for the
 * fortieth time should not have to find a cursor to do it. `MenuButtons` is
 * that, and every other menu has one too.
 */

import type { IntoxicationLevel } from '../config/intoxication';
import { PLAYER_NAME, PLAYER_TAGLINE } from '../config/player';
import { formatTime } from '../utils/format';
import type { RunResult } from '../score/ScoreRepository';
import { LeaderboardPanel } from './LeaderboardPanel';
import { MenuButtons } from './MenuButtons';
import { button, el, setHidden, setText } from './dom';

/**
 * The quotes are not decoration: the runner is `Koodinimi "Kimmo"` on the
 * subtitle right above this, and the venue writes every koodinimi that way -
 * the two lappu taped over the beds behind him say `"Kimmo"` and `"Juppe"`.
 * Written bare, the name reads as somebody's actual first name rather than as
 * the alias it is.
 */
const WAKE_CTA = 'PAINA MITÄ TAHANSA NÄPPÄINTÄ HERÄTTÄÄKSESI "KIMMO"';

const MENU_HINT =
  'NUOLET valitsevat. ENTER vahvistaa.\n' +
  'Tulos tallentuu valitun seedin, alkoholimäärän ja muiden päihteiden mukaiseen tulostauluun.';
const LOCKED_HINT = 'ESC tuo hiiren takaisin tähän valikkoon.';

export interface ReadyScreenActions {
  /** Hand the mouse to the game. The run starts on the next key press. */
  start: () => void;
  openSettings: () => void;
  openHelp: () => void;
  openResults: () => void;
}

export class ReadyScreen {
  readonly root = el('div', 'overlay overlay--menu overlay--clickthrough');
  readonly board = new LeaderboardPanel('Tulostaulu · Top 10', 'top10');

  private readonly actions = el('div', 'menu__actions');
  /** Menu actions for arrows and Enter. */
  readonly buttons: MenuButtons;
  private readonly cta = el('p', 'menu__cta', WAKE_CTA);
  private readonly hint = el('p', 'menu__hint', MENU_HINT);
  private readonly personalBest = el('span', 'stat__value', '--:--.---');
  private readonly seed = el('span', 'stat__value', '0');
  private readonly dose = el('span', 'stat__value');
  private readonly runner = el('span', 'stat__value');

  constructor(actions: ReadyScreenActions) {
    const buttons = [
      button('ALOITA', () => actions.start(), 'btn btn--primary'),
      button('TULOKSET', () => actions.openResults()),
      button('ASETUKSET', () => actions.openSettings()),
      button('OHJEET', () => actions.openHelp()),
    ];
    this.buttons = new MenuButtons(buttons);
    this.board.onButtonsChanged = (rows) => this.buttons.setButtons([...buttons, ...rows]);
    this.actions.append(...buttons);

    const brand = el('div', 'menu__brand');
    brand.append(
      el('p', 'menu__eyebrow', 'SPEEDRUN · EINON PIRTTI'),
      el('h1', 'menu__title', 'LANIAAMU'),
      el('div', 'menu__rule'),
      el('p', 'menu__subtitle', PLAYER_NAME),
      el('p', 'menu__tagline', PLAYER_TAGLINE),
      this.actions,
      this.cta,
      this.hint,
    );

    const stats = el('div', 'stats');
    stats.append(
      stat('NIMIMERKKI', this.runner),
      stat('ENNÄTYS', this.personalBest),
      stat('ANNOS', this.dose),
      stat('SEED', this.seed),
    );

    const side = el('div', 'menu__side');
    side.append(stats, this.board.root);

    const layout = el('div', 'menu');
    layout.append(brand, side);
    this.root.appendChild(layout);
  }

  mount(parent: HTMLElement): void {
    parent.appendChild(this.root);
  }

  update(
    pb: RunResult | null,
    pointerLocked: boolean,
    level: IntoxicationLevel,
    playerName: string,
    seed: number,
  ): void {
    setText(this.personalBest, pb ? formatTime(pb.timeMs) : '--:--.---');
    setText(this.dose, `${level.alcoholPromille.toFixed(2)} ‰ · ${level.drugsPercent} %`);
    setText(this.runner, playerName || 'ei asetettu');
    setText(this.seed, String(seed));
    this.runner.classList.toggle('stat__value--muted', playerName.length === 0);

    // Buttons are unreachable behind pointer lock - the cursor is inside the
    // game - so the menu stands down rather than sitting there uninvitingly.
    setHidden(this.actions, pointerLocked);
    // With the mouse in the game there is nothing left to highlight - and the
    // highlight should not still be sitting on OHJEET when Esc hands the menu
    // back, because whoever comes back is far likelier to want ALOITA.
    this.buttons.setActive(!pointerLocked);
    setHidden(this.cta, !pointerLocked);
    setText(this.hint, pointerLocked ? LOCKED_HINT : MENU_HINT);
  }

  setVisible(visible: boolean): void {
    setHidden(this.root, !visible);
  }
}

function stat(label: string, value: HTMLElement): HTMLElement {
  const row = el('div', 'stat');
  row.append(el('span', 'stat__label', label), value);
  return row;
}
